/**
 * hook for basic page navigation actions like first, last, next, previous
 * provides navigation functions and state for moving through metadoc pages
 */
import { useCallback, useMemo } from "react";

type UsePageNavigationParams = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  minPage?: number;
  maxPage?: number;
  visiblePageNumbers?: number[];
};

type UsePageNavigationReturn = {
  goToFirstPage: () => void;
  goToLastPage: () => void;
  goToPreviousPage: () => void;
  goToNextPage: () => void;
  goBack10Pages: () => void;
  goForward10Pages: () => void;
  canGoBack: boolean;
  canGoForward: boolean;
};

export const usePageNavigation = ({
  currentPage,
  totalPages,
  onPageChange,
  minPage = 1,
  maxPage = totalPages,
  visiblePageNumbers,
}: UsePageNavigationParams): UsePageNavigationReturn => {
  const availablePages = useMemo(() => {
    if (visiblePageNumbers) {
      return visiblePageNumbers;
    }

    const pageCount = Math.max(maxPage - minPage + 1, 0);
    return Array.from({ length: pageCount }, (_, index) => minPage + index);
  }, [maxPage, minPage, visiblePageNumbers]);
  const currentPageIndex = availablePages.indexOf(currentPage);
  const canGoBack = currentPageIndex > 0;
  const canGoForward =
    currentPageIndex >= 0 && currentPageIndex < availablePages.length - 1;

  const goToFirstPage = useCallback(() => {
    if (availablePages.length === 0) {
      return;
    }

    onPageChange(availablePages[0]);
  }, [availablePages, onPageChange]);

  const goToLastPage = useCallback(() => {
    if (availablePages.length === 0) {
      return;
    }

    onPageChange(availablePages[availablePages.length - 1]);
  }, [availablePages, onPageChange]);

  const goToPreviousPage = useCallback(() => {
    if (currentPageIndex > 0) {
      onPageChange(availablePages[currentPageIndex - 1]);
    }
  }, [availablePages, currentPageIndex, onPageChange]);

  const goToNextPage = useCallback(() => {
    if (currentPageIndex >= 0 && currentPageIndex < availablePages.length - 1) {
      onPageChange(availablePages[currentPageIndex + 1]);
    }
  }, [availablePages, currentPageIndex, onPageChange]);

  const goBack10Pages = useCallback(() => {
    if (availablePages.length === 0) {
      return;
    }

    const targetIndex = Math.max(currentPageIndex - 10, 0);
    onPageChange(availablePages[targetIndex]);
  }, [availablePages, currentPageIndex, onPageChange]);

  const goForward10Pages = useCallback(() => {
    if (availablePages.length === 0) {
      return;
    }

    const targetIndex = Math.min(
      currentPageIndex + 10,
      availablePages.length - 1,
    );
    onPageChange(availablePages[targetIndex]);
  }, [availablePages, currentPageIndex, onPageChange]);

  return useMemo(
    () => ({
      goToFirstPage,
      goToLastPage,
      goToPreviousPage,
      goToNextPage,
      goBack10Pages,
      goForward10Pages,
      canGoBack,
      canGoForward,
    }),
    [
      goToFirstPage,
      goToLastPage,
      goToPreviousPage,
      goToNextPage,
      goBack10Pages,
      goForward10Pages,
      canGoBack,
      canGoForward,
    ],
  );
};
